import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { QRCodeCanvas } from 'qrcode.react';
import { ClipboardList, Check, X, Copy, Link2, Mail, Phone, Briefcase, Download, Lock, Unlock, CalendarClock, QrCode } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS = {
  pendiente: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-700' },
  aprobado: { label: 'Dado de alta', cls: 'bg-emerald-100 text-emerald-700' },
  rechazado: { label: 'Rechazado', cls: 'bg-red-100 text-red-700' },
};

const CoursePreinscriptions = ({ courseId, courseType, onApproved }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [settings, setSettings] = useState({ closed: false, deadline: null, open: true, closed_reason: null });
  const [savingSettings, setSavingSettings] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const publicLink = `${window.location.origin}/preinscripcion/${courseType}/${courseId}`;

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/preinscriptions`, { params: { course_id: courseId } });
      setItems(res.data || []);
    } catch (e) { /* noop */ }
  };
  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/preinscriptions/settings/${courseType}/${courseId}`);
      setSettings(res.data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { if (courseId) { load(); loadSettings(); } }, [courseId]); // eslint-disable-line

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API_URL}/preinscriptions/settings/${courseType}/${courseId}`, patch);
      setSettings(res.data);
      toast.success('Ajustes de preinscripción actualizados');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); } finally { setSavingSettings(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicLink); toast.success('Enlace copiado al portapapeles'); }
    catch (e) { toast.error('No se pudo copiar'); }
  };
  const downloadQr = () => {
    const canvas = document.getElementById('preinscription-qr-canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `qr_preinscripcion_${courseId}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const downloadPoster = async () => {
    try {
      const res = await axios.get(`${API_URL}/preinscriptions/poster/${courseType}/${courseId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `cartel_preinscripcion_${courseId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('No se pudo generar el cartel'); }
  };
  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API_URL}/preinscriptions/export`, { params: { course_id: courseId }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `preinscripciones_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('No se pudo exportar'); }
  };
  const approve = async (p) => {
    setBusy(p.id);
    try {
      const res = await axios.post(`${API_URL}/preinscriptions/${p.id}/approve`);
      toast.success(res.data?.credentials_sent ? 'Alumno dado de alta y credenciales enviadas por correo.' : 'Alumno dado de alta correctamente.');
      load();
      if (onApproved) onApproved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al dar de alta'); } finally { setBusy(null); }
  };
  const reject = async (p) => {
    setBusy(p.id);
    try { await axios.post(`${API_URL}/preinscriptions/${p.id}/reject`); toast.success('Preinscripción rechazada'); load(); }
    catch (e) { toast.error('Error al rechazar'); } finally { setBusy(null); }
  };

  const pending = items.filter((i) => i.status === 'pendiente');

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="course-preinscriptions">
      <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-[#004E89]" />
          <h2 className="font-semibold text-xl text-slate-900">Preinscripciones</h2>
          {pending.length > 0 && <Badge className="bg-amber-100 text-amber-700">{pending.length} pendiente{pending.length !== 1 ? 's' : ''}</Badge>}
          {settings.open === false && <Badge className="bg-slate-200 text-slate-600">Cerradas</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={items.length === 0} className="border-slate-300 text-slate-600" data-testid="course-export-preinscriptions">
            <Download className="w-4 h-4 mr-1" /> Exportar CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => setQrOpen(true)} className="border-slate-300 text-slate-600" data-testid="open-preinscription-qr">
            <QrCode className="w-4 h-4 mr-1" /> QR
          </Button>
          <Button variant="outline" size="sm" onClick={copyLink} className="border-[#004E89] text-[#004E89] hover:bg-[#004E89] hover:text-white" data-testid="copy-preinscription-link">
            <Copy className="w-4 h-4 mr-1" /> Copiar enlace
          </Button>
        </div>
      </div>

      <div className="px-6 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-500">
        <Link2 className="w-4 h-4 flex-shrink-0" />
        <span className="truncate" data-testid="preinscription-public-link">{publicLink}</span>
      </div>

      <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="preinscription-settings">
        <div className="flex items-center gap-3">
          {settings.closed ? <Lock className="w-4 h-4 text-slate-400" /> : <Unlock className="w-4 h-4 text-emerald-500" />}
          <div>
            <p className="text-sm font-medium text-slate-700">Admitir preinscripciones</p>
            <p className="text-xs text-slate-400">{settings.closed ? 'El enlace público está desactivado' : 'El enlace público está activo'}</p>
          </div>
          <Switch checked={!settings.closed} disabled={savingSettings} onCheckedChange={(v) => saveSettings({ closed: !v })} data-testid="toggle-preinscription-open" />
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <CalendarClock className="w-4 h-4 text-slate-400" />
          <label className="text-sm text-slate-600">Fecha límite:</label>
          <Input type="date" value={settings.deadline ? String(settings.deadline).slice(0, 10) : ''} disabled={savingSettings}
            onChange={(e) => saveSettings({ deadline: e.target.value })} className="w-40 h-9" data-testid="preinscription-deadline" />
          {settings.deadline && (
            <Button variant="ghost" size="sm" onClick={() => saveSettings({ deadline: '' })} className="text-slate-400 hover:text-red-600 px-2" data-testid="clear-deadline">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <p className="px-6 py-8 text-center text-slate-400 text-sm">Aún no hay preinscripciones. Comparte el enlace para que los alumnos se preinscriban.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((p) => (
            <div key={p.id} className="px-6 py-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="course-preinscription-row">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-900">{p.nombre} {p.apellidos}</p>
                  <Badge className={STATUS[p.status]?.cls}>{STATUS[p.status]?.label}</Badge>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-1">
                  <span>DNI: {p.documento_identidad}</span>
                  <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {p.email}</span>
                  <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {p.telefono}</span>
                  <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {p.categoria_profesional}</span>
                  <span>Centro: {p.centro_trabajo}</span>
                </div>
              </div>
              {p.status === 'pendiente' && (
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => approve(p)} disabled={busy === p.id} className="bg-emerald-600 hover:bg-emerald-700" data-testid="course-pre-approve">
                    <Check className="w-4 h-4 mr-1" /> Dar de alta
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject(p)} disabled={busy === p.id} className="border-red-200 text-red-600 hover:bg-red-50" data-testid="course-pre-reject">
                    <X className="w-4 h-4 mr-1" /> Rechazar
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="preinscription-qr-dialog">
          <DialogHeader>
            <DialogTitle>Código QR de preinscripción</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="p-4 bg-white rounded-xl border border-slate-200">
              <QRCodeCanvas id="preinscription-qr-canvas" value={publicLink} size={240} level="M" includeMargin />
            </div>
            <p className="text-xs text-slate-400 text-center break-all px-4">{publicLink}</p>
            <Button onClick={downloadQr} className="w-full bg-[#004E89] hover:bg-[#003B66]" data-testid="download-preinscription-qr">
              <Download className="w-4 h-4 mr-1" /> Descargar QR (PNG)
            </Button>
            <Button onClick={downloadPoster} variant="outline" className="w-full border-[#004E89] text-[#004E89] hover:bg-[#004E89] hover:text-white" data-testid="download-preinscription-poster">
              <Download className="w-4 h-4 mr-1" /> Descargar cartel A4 (PDF)
            </Button>
            <p className="text-xs text-slate-400 text-center">Imprímelo en carteles para que los alumnos se preinscriban escaneándolo.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoursePreinscriptions;
